import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient, } from '@angular/common/http';
import {catchError, Observable, of, Subject, tap} from 'rxjs';

import { ApiConfig } from './api-config';
import { Contract, ContractComponent, ContractContact, ContractNote, Customer } from '../models';

@Injectable({
  providedIn: 'root'
})
export class ContractsService {

  private contractChange: Subject<void> = new Subject<void>();

  constructor(private http: HttpClient,
              private datePipe: DatePipe) {
  }

  contractChanged(): void {
    this.contractChange.next();
  }

  onContractChange(): Observable<void> {
    return this.contractChange.asObservable();
  }

  getAll(): Observable<Contract[]> {
    const url = ApiConfig.getApiUrl('/contract/contracts.json');
    return this.http.get<Contract[]>(url)
      .pipe(
        tap(contracts => console.log('ContractsService.getAll() =>', contracts)),
        catchError(this.handleError<any>('getAll', []))
      );
  }

  get(id: number): Observable<Contract> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${id}.json`);
    return this.http.get<Contract>(url)
      .pipe(
        tap(contract => console.log('ContractsService.get() =>', contract)),
        catchError(this.handleError<any>('get', null))
      );
  }

  createContract(contract: Contract): Observable<Contract> {
    const url = ApiConfig.getApiUrl('/contract/contracts.json');
    const payload: Contract = {
      ...contract,
      activated_on: this.formatDate(contract?.activated_on)
    } as Contract;
    return this.http.post<Contract[]>(url, {contract: payload})
      .pipe(
        tap(contracts => console.log('ContractsService.createContract() =>', contracts)),
        catchError(this.handleError<any>('createContract', null))
      );
  }

  updateContract(contract: Contract): Observable<Contract> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contract.id}.json`);
    const payload: Contract = {
      ...contract,
      activated_on: this.formatDate(contract?.activated_on)
    } as Contract;
    return this.http.patch<Contract>(url, {contract: payload})
      .pipe(
        tap(contracts => console.log('ContractsService.updateContract() =>', contracts)),
        catchError(this.handleError<any>('updateContract', null))
      );
  }

  addCustomer(contractId: Contract['id'], customerId: Customer['id']): Observable<Contract> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/customers`);
    return this.http.post<Contract>(url, { customer: { customer_id: customerId } })
      .pipe(
        tap(contract => console.log('ContractsService.addCustomer() =>', contract)),
        catchError(this.handleError<any>('addCustomer', null))
      );
  }

  removeCustomer(contractId: Contract['id'], customerId: Customer['id']): Observable<Contract> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/customers/${customerId}`);
    return this.http.delete<Contract>(url, {})
      .pipe(
        tap(contract => console.log('ContractsService.removeCustomer() =>', contract)),
        catchError(this.handleError<any>('removeCustomer', null))
      );
  }

  availableCustomers(): Observable<Customer[]> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/customers`);
    return this.http.get<Customer[]>(url)
      .pipe(
        tap(customers => console.log('ContractsService.availableCustomers() =>', customers)),
        catchError(this.handleError<any>('availableCustomers', []))
      );
  }

  updateComponent(contractId: Contract['id'], component: ContractComponent): Observable<ContractComponent> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/components/${component.id}`);
    const payload = {
      ...component,
      activated_on: this.formatDate(component?.activated_on),
      deactivated_on: component?.deactivated_on ? this.formatDate(component.deactivated_on) : null,
    } as ContractComponent;
    return this.http.patch<ContractComponent>(url, { component: payload })
      .pipe(
        tap(component => console.log('ContractsService.updateComponent() =>', component)),
        catchError(this.handleError<any>('updateComponent', null))
      );
  }

  addComponent(contractId: Contract['id'], component: any): Observable<ContractComponent> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/components`);
    const payload = {
      ...component,
      activated_on: this.formatDate(component?.activated_on),
      deactivated_on: component?.deactivated_on ? this.formatDate(component.deactivated_on) : null,
    };
    return this.http.post<ContractComponent>(url, { component: payload })
      .pipe(
        tap(component => console.log('ContractsService.addComponent() =>', component)),
        catchError(this.handleError<any>('addComponent', null))
      );
  }

  removeComponent(contractId: Contract['id'], componentId: ContractComponent['id']): Observable<void> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/components/${componentId}`);
    return this.http.delete<void>(url, {})
      .pipe(
        tap(component => console.log('ContractsService.removeComponent() =>', component)),
        catchError(this.handleError<any>('removeComponent', null))
      );
  }

  addContact(contractId: Contract['id'], contact: ContractContact): Observable<ContractContact> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/contacts`);
    return this.http.post<ContractContact>(url, { contact })
      .pipe(
        tap(contact => console.log('ContractsService.addContact() =>', contact)),
        catchError(this.handleError<any>('addContact', null))
      );
  }

  updateContact(contractId: Contract['id'], contact: ContractContact): Observable<ContractContact> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/contacts/${contact.id}`);
    return this.http.patch<ContractContact>(url, { contact })
      .pipe(
        tap(contact => console.log('ContractsService.updateContact() =>', contact)),
        catchError(this.handleError<any>('updateContact', null))
      );
  }

  removeContact(contractId: Contract['id'], contact: ContractContact): Observable<void> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/contacts/${contact.id}`);
    return this.http.delete<void>(url);
  }

  addNote(contractId: Contract['id'], note: string): Observable<ContractNote> {
    const url = ApiConfig.getApiUrl(`/contract/contracts/${contractId}/notes`);
    return this.http.post<ContractNote>(url, { note: { text: note } })
      .pipe(
        tap(note => console.log('ContractsService.addNote() =>', note)),
        catchError(this.handleError<any>('addNote', null))
      );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error); // log to console instead
      console.error(`${operation} failed: ${error.message}`);
      return of(result as T);
    };
  }

  // Ensure dates are formatted as yyyy.MM.dd using Angular DatePipe
  private formatDate(value: any): any {
    if (!value) return value;
    // If already in yyyy.MM.dd (length 10 with two dots), return as is
    if (typeof value === 'string' && /^\d{4}\.\d{2}\.\d{2}$/.test(value)) return value;
    const date = value instanceof Date ? value : new Date(value);
    const transformed = this.datePipe.transform(date, 'yyyy.MM.dd');
    return transformed ?? value;
  }

}
